import { z } from 'zod';

/**
 * Onboarding-form Zod schemas for FR-ONB-1 (public onboarding form).
 *
 * These schemas are consumed by:
 *   - the merchant onboarding form (form validation in /app/web)
 *   - the API request validator (/app/api/src/routes/onboarding.ts)
 *
 * Keep the field set tight at M1. M3 expands onboarding with EIN, KYB
 * documents, and electronic signature per FR-ONB-3.
 */

export const usStateCode = z.enum([
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
]);
export type UsStateCode = z.infer<typeof usStateCode>;

export const businessType = z.enum([
  'cafe',
  'restaurant',
  'bar',
  'retail',
  'salon',
  'service',
  'fitness',
  'professional',
  'other',
]);
export type BusinessType = z.infer<typeof businessType>;

/**
 * Monthly stablecoin volume estimate. Buckets reflect the PRD target SMB
 * profile ($3K–$15K/mo) per business plan §5.2.
 */
export const monthlyVolumeEstimate = z.enum([
  'under_3k',
  'between_3k_15k',
  'between_15k_50k',
  'over_50k',
]);
export type MonthlyVolumeEstimate = z.infer<typeof monthlyVolumeEstimate>;

export const businessAddress = z.object({
  street: z.string().trim().min(1, 'Street is required').max(200),
  unit: z.string().trim().max(50).optional(),
  city: z.string().trim().min(1, 'City is required').max(100),
  state: usStateCode,
  zip: z
    .string()
    .trim()
    .regex(/^\d{5}(-\d{4})?$/, 'Enter a 5- or 9-digit US ZIP code'),
});
export type BusinessAddress = z.infer<typeof businessAddress>;

/**
 * Permissive US phone-number regex — accepts common formats including
 * (212) 555-0100, 212-555-0100, 2125550100, +1 212 555 0100, and 1-212-555-0100.
 * Server normalizes to E.164 (`+1XXXXXXXXXX`) before persisting.
 */
const usPhoneRegex = /^\+?1?[\s.-]?\(?(\d{3})\)?[\s.-]?(\d{3})[\s.-]?(\d{4})$/;

/**
 * Normalize a permissive US phone string to E.164 (+1XXXXXXXXXX). Caller has
 * already validated against `usPhoneRegex`; this strips formatting and prepends
 * the country code.
 */
export function normalizeUsPhone(input: string): string {
  const digits = input.replace(/\D/g, '');
  // Strip a leading '1' if present (regex allowed it as optional).
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return `+1${ten}`;
}

export const onboardingStartInput = z.object({
  businessName: z.string().trim().min(1, 'Business name is required').max(200),
  ownerName: z.string().trim().min(1, 'Owner name is required').max(100),
  ownerEmail: z.string().trim().toLowerCase().email('Enter a valid email').max(200),
  ownerPhone: z
    .string()
    .trim()
    .regex(usPhoneRegex, 'Enter a US phone number (e.g., (212) 555-0100)'),
  businessType,
  businessAddress,
  monthlyVolumeEstimate,
});
export type OnboardingStartInput = z.infer<typeof onboardingStartInput>;
